import { dateFormats } from '../../consts'
import moment from 'moment-timezone'

/**
 * Converts a date to the specified timezone
 * @param {Date|string} date - Date to convert
 * @param {string} tzString - Timezone string (default: from dateFormats)
 * @returns {Date} Date in the specified timezone
 */
export function convertTZ (date, tzString = dateFormats.timezone) {
  return moment(date).tz(tzString).toDate()
}

/**
 * Gets the start of the current month in the specified timezone
 * @param {string} timezone - Timezone string (default: from dateFormats)
 * @returns {Date} Start of month date
 */
export function getMonthStart (timezone = dateFormats.timezone) {
  return moment().tz(timezone).startOf('month').toDate()
}

/**
 * Gets the current date (start of today) in the specified timezone
 * @param {string} timezone - Timezone string (default: from dateFormats)
 * @returns {Date} Start of today in the specified timezone
 */
export function getCurrentDate (timezone = dateFormats.timezone) {
  return moment().tz(timezone).startOf('day').toDate()
}

/**
 * Gets the end of a specific date in the specified timezone
 * @param {Date|string} date - Date to get end of
 * @param {string} timezone - Timezone string (default: from dateFormats)
 * @returns {Date} End of the specified date (23:59:59.999) in the timezone
 */
export function getEndOfDate (date, timezone = dateFormats.timezone) {
  return moment(date).tz(timezone).endOf('day').toDate()
}

/**
 * Formats a date as an ISO string
 * @param {Date} date - Date to format
 * @returns {string} ISO string
 */
export function dateToISOString (date) {
  return date ? date.toISOString() : null
}

/**
 * Checks if the start date is after the end date
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {boolean} True if start date is after end date
 */
export function isInvalidDateRange (startDate, endDate) {
  return new Date(startDate) > new Date(endDate)
}

/**
 * Parse a YYYY-MM-DD date string in Eastern timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date|null} Parsed date or null if invalid
 */
export function parseDateStringInEastern (dateString) {
  if (!dateString) return null
  const dateMoment = moment.tz(dateString, 'YYYY-MM-DD', dateFormats.timezone)
  return dateMoment.isValid() ? dateMoment.toDate() : null
}

/**
 * Extract and normalize a date value from an event, string, or Date object.
 * Returns start of day in Eastern timezone.
 * @param {Event|Date|string|null} eventOrValue - Input that may contain a date
 * @returns {Date} Normalized date at start of day in Eastern TZ (falls back to today)
 */
export function parseDateValue (eventOrValue) {
  const value = eventOrValue?.target?.value || eventOrValue?.value || eventOrValue
  let dateMoment = null

  if (value instanceof Date && !isNaN(value.getTime())) {
    dateMoment = moment(value).tz(dateFormats.timezone).startOf('day')
  } else if (typeof value === 'string' && value) {
    dateMoment = moment.tz(value, 'YYYY-MM-DD', dateFormats.timezone)
  }

  if (!dateMoment || !dateMoment.isValid()) {
    dateMoment = moment().tz(dateFormats.timezone).startOf('day')
  }

  return dateMoment.toDate()
}

/**
 * Convert a date to an exclusive end date ISO string (start of next day in Eastern TZ).
 * Used for date range queries where the end date should include the full selected day.
 * @param {Date} date - End date
 * @returns {string|null} ISO string of start of next day, or null if invalid
 */
export function toExclusiveEndDateISO (date) {
  if (!date) return null
  return moment(date).tz(dateFormats.timezone).add(1, 'day').startOf('day').toDate().toISOString()
}

/**
 * Default Intl options for call_date display — matches the CallResultsTable
 * column (12-hour clock, seconds shown).
 */
const CALL_DATE_DISPLAY_OPTIONS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
}

/**
 * Formats a naive-ET call_date for display. call_date is stored naive ET and pg
 * stamps it as UTC on the wire, so we format with timeZone:'UTC' to print back
 * the original ET wall-clock (matching the CallResultsTable column).
 * @param {string|Date|null} dateStr - Raw call_date value
 * @param {Object} [opts]
 * @param {string} [opts.fallback] - Returned when dateStr is falsy (default 'N/A')
 * @param {Object} [opts.options] - Intl.DateTimeFormat options override
 * @returns {string} Formatted ET wall-clock string
 */
export function formatCallDateET (dateStr, { fallback = 'N/A', options = CALL_DATE_DISPLAY_OPTIONS } = {}) {
  if (!dateStr) return fallback
  return new Date(dateStr).toLocaleString('en-US', { ...options, timeZone: 'UTC' })
}
